import { Field } from '@/components/builder/inspector-fields';
import { Section } from '@/components/builder/inspector-controls';
import { BLOCK_ICON } from '@/components/builder/layers-tree';
import { blockSchema } from '@personalizehq/schemas';
import { findNode, findStep, nodeLabel, type SessionStore, type Step } from '@personalizehq/editor';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import { Layers3, X } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from 'zustand';

const MIXED = '__mixed__';

export function MultiInspector({
  session,
  step,
  selection,
}: {
  session: SessionStore;
  step: Step;
  selection: string[];
}): React.JSX.Element {
  const doc = useStore(session, (state) => state.doc);
  const nodes = selection.flatMap((id) => {
    const hit = findNode(step.root, id);
    return hit ? [hit.node] : [];
  });
  const allInScroll = selection.every((id) => hasScrollAncestor(step.root, id));

  if (nodes.length === 0) {
    return (
      <div className="grid h-full place-items-center px-6 text-center text-xs text-muted-foreground">
        <div>
          <p>The selected components no longer exist in this step.</p>
          <button type="button" className="mt-3 rounded-md border px-3 py-1.5 text-foreground hover:bg-muted" onClick={() => session.getState().select(null)}>
            Clear selection
          </button>
        </div>
      </div>
    );
  }

  const set = (field: string, mutate: (node: BlockNode) => void): void => {
    const candidates = nodes.map((node) => {
      const clone = JSON.parse(JSON.stringify(node)) as BlockNode;
      mutate(clone);
      return clone;
    });
    if (candidates.some((node) => !blockSchema.safeParse(node).success)) {
      toast.error('That value is not valid for every selected component.');
      return;
    }
    session.getState().applyChange((draft) => {
      // Doc-wide: the focused step may live inside a variant (ADR 0015).
      const root = findStep(draft, step.id)!.root;
      selection.forEach((id) => {
        const target = findNode(root, id);
        if (target) mutate(target.node);
      });
    }, `${selection.slice().sort().join(',')}.${field}`);
  };

  const value = <T,>(read: (node: BlockNode) => T): T | typeof MIXED => {
    const first = read(nodes[0]!);
    return nodes.every((node) => JSON.stringify(read(node)) === JSON.stringify(first)) ? first : MIXED;
  };
  const bg = value((node) => node.style?.bg);
  const opacity = value((node) => node.style?.opacity);
  // Paint-token forms (a `radii`/`elevations` key, ADR 0014) are not batch-editable inline — coerce
  // them to "mixed" so these numeric/token rows only drive the inline literal forms.
  const radius = value((node) => (typeof node.style?.radius === 'string' ? MIXED : node.style?.radius));
  const border = value((node) => node.style?.border);
  const shadow = value((node) => (typeof node.style?.shadow === 'string' ? MIXED : node.style?.shadow));
  const animate = value((node) => node.style?.animate);
  const a11yLabel = value((node) => node.a11y_label);
  const a11yHidden = value((node) => node.a11y_hidden);
  const requires = value((node) => node.requires);

  const assignStyle = (node: BlockNode, patch: NonNullable<BlockNode['style']>): void => {
    node.style = { ...(node.style ?? {}), ...patch };
    if (Object.values(node.style).every((entry) => entry === undefined)) delete node.style;
  };
  const assignMotion = <K extends keyof NonNullable<NonNullable<BlockNode['style']>['animate']>>(
    node: BlockNode,
    key: K,
    next: NonNullable<NonNullable<BlockNode['style']>['animate']>[K] | undefined
  ): void => {
    const animate = { ...(node.style?.animate ?? {}) };
    if (next === undefined) delete animate[key];
    else animate[key] = next;
    assignStyle(node, { animate: Object.keys(animate).length > 0 ? animate : undefined });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2.5 border-b px-3">
        <span className="grid size-8 place-items-center rounded-md bg-muted text-muted-foreground"><Layers3 className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold">{selection.length} components selected</div>
          <div className="text-[10px] uppercase text-muted-foreground">Batch editing</div>
        </div>
        <button type="button" aria-label="Clear selection" className="grid size-7 place-items-center rounded-md hover:bg-muted" onClick={() => session.getState().select(null)}><X className="size-4" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section title="Selected components">
          <div className="space-y-1">
            {selection.map((id) => {
              const node = findNode(step.root, id)?.node;
              if (!node) return null;
              const Icon = BLOCK_ICON[node.block];
              return <div key={id} className="flex items-center gap-2 rounded px-1.5 py-1 text-xs"><Icon className="size-3.5 text-muted-foreground" /><span className="min-w-0 flex-1 truncate">{nodeLabel(node, doc.labels)}</span><button type="button" aria-label={`Remove ${nodeLabel(node, doc.labels)} from selection`} className="rounded p-1 hover:bg-muted" onClick={() => session.getState().select(id, 'toggle')}><X className="size-3" /></button></div>;
            })}
          </div>
        </Section>
        <Section title="Style">
          <Field label="background">
            <select aria-label="batch background" className="h-8 w-full rounded-md border bg-background px-2 text-xs" value={typeof bg === 'string' ? bg : bg === undefined ? '' : MIXED} onChange={(event) => set('style.bg', (node) => assignStyle(node, { bg: event.target.value ? event.target.value : undefined }))}>
              {bg === MIXED || (bg && typeof bg !== 'string') ? <option value={MIXED}>— Mixed —</option> : null}
              <option value="">None</option>
              {Object.keys(doc.theme.colors).map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </Field>
          <Numeric label="opacity" value={opacity === MIXED ? MIXED : opacity} min={0} max={1} step={0.05} onChange={(next) => set('style.opacity', (node) => assignStyle(node, { opacity: next }))} />
          <Numeric label="radius" value={radius === MIXED || typeof radius === 'object' || typeof radius === 'string' ? MIXED : radius} min={0} onChange={(next) => set('style.radius', (node) => assignStyle(node, { radius: next }))} />
          <Numeric label="border width" value={border === MIXED ? MIXED : border?.width} min={0} onChange={(next) => set('style.border.width', (node) => assignStyle(node, { border: next === undefined ? undefined : { width: next, color: node.style?.border?.color ?? Object.keys(doc.theme.colors)[0] ?? '' } }))} />
          <Token label="border color" value={border === MIXED ? MIXED : border?.color} tokens={Object.keys(doc.theme.colors)} onChange={(next) => set('style.border.color', (node) => assignStyle(node, { border: next ? { width: node.style?.border?.width ?? 1, color: next } : undefined }))} />
          <Numeric label="shadow blur" value={shadow === MIXED ? MIXED : shadow?.blur} min={0} onChange={(next) => set('style.shadow.blur', (node) => { const cur = typeof node.style?.shadow === 'object' ? node.style.shadow : undefined; assignStyle(node, { shadow: next === undefined ? undefined : { x: cur?.x, y: cur?.y ?? 2, blur: next, spread: cur?.spread ?? 0, color: cur?.color ?? Object.keys(doc.theme.colors)[0] ?? '' } }); })} />
          <Token label="shadow color" value={shadow === MIXED ? MIXED : shadow?.color} tokens={Object.keys(doc.theme.colors)} onChange={(next) => set('style.shadow.color', (node) => { const cur = typeof node.style?.shadow === 'object' ? node.style.shadow : undefined; assignStyle(node, { shadow: next ? { x: cur?.x, y: cur?.y ?? 2, blur: cur?.blur ?? 8, spread: cur?.spread ?? 0, color: next } : undefined }); })} />
        </Section>
        <Section title="Motion">
          <Token label="entry" value={animate === MIXED ? MIXED : animate?.entry?.kind} tokens={['fade', 'fade_up', 'scale_in']} onChange={(next) => set('style.animate.entry', (node) => assignMotion(node, 'entry', next ? { kind: next as 'fade' | 'fade_up' | 'scale_in', duration_ms: node.style?.animate?.entry?.duration_ms ?? 250 } : undefined))} />
          <Numeric label="stagger ms" value={animate === MIXED ? MIXED : animate?.stagger_ms} min={0} max={1000} onChange={(next) => set('style.animate.stagger_ms', (node) => assignMotion(node, 'stagger_ms', next))} />
          {allInScroll ? <Numeric label="parallax" value={animate === MIXED ? MIXED : animate?.parallax} min={-1} max={1} step={0.1} onChange={(next) => set('style.animate.parallax', (node) => assignMotion(node, 'parallax', next))} /> : null}
        </Section>
        <Section title="Accessibility">
          <Field label="label"><input aria-label="batch accessibility label" className="h-8 w-full rounded-md border bg-background px-2 text-xs" placeholder={a11yLabel === MIXED ? 'Mixed' : ''} value={a11yLabel === MIXED ? '' : a11yLabel ?? ''} onChange={(event) => set('a11y_label', (node) => { node.a11y_label = event.target.value || undefined; })} /></Field>
          <Token label="hidden" value={a11yHidden === MIXED ? MIXED : a11yHidden === undefined ? '' : String(a11yHidden)} tokens={['true', 'false']} onChange={(next) => set('a11y_hidden', (node) => { node.a11y_hidden = next === '' ? undefined : next === 'true'; })} />
        </Section>
        <Section title="Compatibility">
          <Field label="requires"><input aria-label="batch requirements" className="h-8 w-full rounded-md border bg-background px-2 text-xs" placeholder={requires === MIXED ? 'Mixed' : 'capability, capability'} value={requires === MIXED ? '' : (requires ?? []).join(', ')} onChange={(event) => set('requires', (node) => { const next = event.target.value.split(',').map((part) => part.trim()).filter(Boolean); node.requires = next.length ? next : undefined; })} /></Field>
        </Section>
      </div>
    </div>
  );
}

function Numeric({ label, value, onChange, ...props }: { label: string; value: number | undefined | typeof MIXED; onChange: (value: number | undefined) => void; min?: number; max?: number; step?: number }): React.JSX.Element {
  return <Field label={label}><input aria-label={`batch ${label}`} type="number" {...props} className="h-8 w-full rounded-md border bg-background px-2 text-xs" placeholder={value === MIXED ? 'Mixed' : ''} value={value === MIXED || value === undefined ? '' : value} onChange={(event) => onChange(event.target.value === '' ? undefined : Number(event.target.value))} /></Field>;
}

function Token({ label, value, tokens, onChange }: { label: string; value: string | undefined | typeof MIXED; tokens: string[]; onChange: (value: string) => void }): React.JSX.Element {
  return <Field label={label}><select aria-label={`batch ${label}`} className="h-8 w-full rounded-md border bg-background px-2 text-xs" value={value ?? ''} onChange={(event) => onChange(event.target.value)}>{value === MIXED ? <option value={MIXED}>— Mixed —</option> : null}<option value="">None</option>{tokens.map((token) => <option key={token} value={token}>{token}</option>)}</select></Field>;
}

function hasScrollAncestor(root: BlockNode, id: string): boolean {
  const walk = (node: BlockNode, inScroll: boolean): boolean => node.id === id
    ? inScroll
    : 'children' in node && node.children.some((child) => walk(child, inScroll || node.block === 'scroll'));
  return walk(root, false);
}
